import { calendarParams } from '@lmt/unicorn-shared/app/constants/calendarParams';
import {
  createRoute,
  CreateRouteResult,
  NumberParam,
  ParamTypes,
  QueryParamConfigMap,
  RequiredNumberParam,
  RequiredStringParam,
  StringParam,
} from 'react-router-url-params';
import { ParamParseKey } from 'react-router';

const tabsPath = '/tabs/:tabId' as const;

function createTabLink<
  ParamKey extends ParamParseKey<Path>,
  Path extends string,
  ParamsConfig extends ParamTypes<ParamKey>,
  SearchParams extends QueryParamConfigMap,
>(
  pattern: Path,
  urlParamsConfig?: Path extends `${string}:${string}`
    ? ParamsConfig
    : undefined,
  searchParamsConfig?: SearchParams,
): CreateRouteResult<ParamKey, Path, ParamsConfig, SearchParams> {
  const result = createRoute(
    `${tabsPath}${pattern}` as any,
    {
      ...(urlParamsConfig ?? {}),
      tabId: StringParam,
    },
    searchParamsConfig,
  ) as any;
  return {
    ...result,
    link: (params: any, search: any) => {
      params = { ...params, tabId: ':tabId' };
      const link = result.link(params, search);
      return link.replace('/tabs/:tabId', '');
    },
  };
}

// to adjust tab title edit useTitleAndIconForTab hook
export const pages = {
  nonTabPage: createRoute('/:tabName/*', {
    tabName: RequiredStringParam,
    ['*']: StringParam,
  }),
  tabs: createRoute('/tabs/:tabId/:tabName/*', {
    tabName: RequiredStringParam,
    tabId: RequiredStringParam,
    ['*']: StringParam,
  }),
  home: createRoute('/'),
  dashboard: createRoute('/dashboard'),
  patients: createRoute('/patients'),
  patientsTab: createTabLink('/patients'),
  patientProfile: createTabLink('/patients/:patientId/*', {
    patientId: RequiredNumberParam,
  }),
  patientProfileWithTab: createTabLink('/patients/:patientId/:tabName/*', {
    patientId: RequiredNumberParam,
    tabName: RequiredStringParam,
  }),
  patientCaseLatest: createTabLink('/patients/:patientId/cases', {
    patientId: RequiredNumberParam,
  }),
  patientCases: createTabLink('/patients/:patientId/cases/:caseId', {
    patientId: RequiredNumberParam,
    caseId: RequiredNumberParam,
  }),
  calendar: createRoute('/calendar'),
  calendarTab: createTabLink('/calendar/*', undefined, calendarParams),
  calendarColorsSettings: createTabLink('/calendar/color-settings'),
  calendarVisitCardSettings: createTabLink('/calendar/visit-card-settings'),
  audit: createTabLink('/audit'),
  mappings: createTabLink('/mappings/*'),
  mappingsWithDeviceAndMeasurement: createTabLink('/mappings', undefined, {
    deviceModel: NumberParam,
    measurementTypeId: NumberParam,
  }),
  dictionary: createTabLink('/dictionaries/:dictionaryType', {
    dictionaryType: RequiredStringParam,
  }),
  dictionaryVisitTypes: createTabLink('/dictionaries/visit-types'),
  dictionaryTags: createTabLink('/dictionaries/tags'),
  users: createTabLink('/users'),
  roles: createTabLink('/roles'),
  dataBlockFields: createTabLink('/data-block-fields', undefined, {
    variant: NumberParam,
    typeId: NumberParam,
    deviceModel: NumberParam,
  }),
  dataBlockTypes: createTabLink('/data-block-types'),
  createPatientDocument: createTabLink(
    '/documents/patient/:patientId/:templateId?',
    {
      patientId: RequiredNumberParam,
      templateId: NumberParam,
    },
  ),
  signalViewer: createTabLink('/signal-viewer/:documentId', {
    documentId: RequiredStringParam,
  }),
  document: createTabLink('/documents/*'),
  documentDesigner: createTabLink(
    '/documents/designer/:templateType/:templateId?',
    {
      templateType: RequiredNumberParam,
      templateId: NumberParam,
    },
  ),
  documentTemplateRules: createTabLink('/document-rules'),
  statisticsDesigner: createTabLink('/statistics/designer'),
  statisticsViewer: createTabLink('/statistics'),
  subtenants: createTabLink('/subtenants'),
  dataBlockSettings: createTabLink('/data-block-settings'),
  doctors: createTabLink('/doctors'),
  attendingDoctors: createTabLink('/attending-doctors'),
  createTenant: createRoute('/create-tenant'),
  settings: createTabLink('/settings'),
  tenantSettings: createTabLink('/tenant-settings'),
  subtenantSettings: createTabLink('/subtenant-settings/', undefined, {
    subtenantId: NumberParam,
  }),
  customDictionary: createTabLink(
    '/settings/custom-dictionaries/:dictionaryId',
    {
      dictionaryId: RequiredNumberParam,
    },
  ),
  miniscreenImporter: createTabLink('/miniscreen-importer'),
  hl7: createTabLink('/hl7/:subPage/*', {
    subPage: RequiredStringParam,
  }),
  hl7Messages: createTabLink('/hl7/messages'),
  hl7Filters: createTabLink('/hl7/filters'),
  hl7Mappings: createTabLink('/hl7/mapping/*'),
  hl7MappingsWithId: createTabLink('/hl7/mapping/:mappingId', {
    mappingId: RequiredNumberParam,
  }),
  hl7MessageTypes: createTabLink('/hl7/message-types'),
  hl7OutgoingTemplates: createTabLink('/hl7/outgoing-templates/*'),
  hl7OutgoingTemplatesWithId: createTabLink(
    '/hl7/outgoing-templates/:templateId',
    {
      templateId: RequiredNumberParam,
    },
  ),
  hl7TemplateRules: createTabLink('/hl7/template-rules'),
  gdt: createTabLink('/gdt/:subPage/*', {
    subPage: RequiredStringParam,
  }),
  gdtOutgoingTemplates: createTabLink('/gdt/outgoing-templates/*'),
  gdtOutgoingTemplatesWithId: createTabLink(
    '/gdt/outgoing-templates/:templateId',
    {
      templateId: RequiredNumberParam,
    },
  ),
  gdtMessages: createTabLink('/gdt/messages'),
  gdtMappings: createTabLink('/gdt/mapping/*'),
  gdtMappingsWithId: createTabLink('/gdt/mapping/:mappingId', {
    mappingId: RequiredNumberParam,
  }),
  gdtTemplateRules: createTabLink('/gdt/template-rules'),
  resources: createTabLink('/resources/:resourceType', {
    resourceType: RequiredNumberParam,
  }),
  patientSearch: createTabLink('/search'),
  mergeAssistant: createTabLink('/merge-assistant'),
  absences: createTabLink('/absences'),
  passwordReset: createRoute('/reset-password', undefined, {
    username: StringParam,
    passwordResetToken: RequiredStringParam,
  }),
  workstationSettings: createTabLink('/workstation-settings'),
};
// to adjust tab title edit useTitleAndIconForTab hook
